import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Monitor, Power, ShieldCheck, UserCog, Building2, LayoutDashboard, BusFront, FileSignature } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { displayName } from "@/lib/format";
import { officerHasConsole, officerAllowedModules } from "@/lib/access";
import { HipaaAckBanner } from "@/components/HipaaAckBanner";

export default function PortalPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [building, setBuilding] = useState({ name: "", address: "", logo_data_url: "" });
  const [ending, setEnding] = useState(false);
  const [shiftStart] = useState(() => new Date());
  const [isSchool, setIsSchool] = useState(false);

  useEffect(() => {
    api.get("/settings/building")
      .then(({ data }) => setBuilding({ ...data, logo_data_url: data.effective_logo_data_url || data.logo_data_url || "" }))
      .catch(() => {});
    api.get("/me/company").then(({ data }) => setIsSchool(["school", "daycare"].includes(data?.type))).catch(() => {});
  }, []);

  const endShift = async () => {
    if (!confirm("End your shift and sign out of this device?")) return;
    setEnding(true);
    await logout();
    toast.success("Shift ended");
    nav("/login");
  };

  const isAdmin = user?.role === "admin";
  const canOffice = isAdmin || user?.role === "officer";

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-10" data-testid="portal-page">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-white">
            {isAdmin ? <ShieldCheck className="h-6 w-6" /> : <UserCog className="h-6 w-6" />}
          </div>
          <div>
            <div className="font-display text-2xl font-bold tracking-tight text-slate-900">
              {isAdmin ? "Admin Portal" : "Officer Portal"}
            </div>
            <div className="text-sm text-slate-500">Manage your shift on this device</div>
          </div>
        </div>
        <div className="-mx-6 mb-6"><HipaaAckBanner /></div>

        {/* Building card */}
        {(building.name || building.logo_data_url) && (
          <section className="rounded-xl border border-slate-200 bg-white p-5 mb-6 flex items-center gap-4" data-testid="portal-building">
            {building.logo_data_url ? (
              <img src={building.logo_data_url} alt="Logo" className="h-14 w-14 object-contain" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-slate-100">
                <Building2 className="h-6 w-6 text-slate-500" />
              </div>
            )}
            <div>
              <div className="font-display text-lg font-semibold text-slate-900">{building.name}</div>
              <div className="text-sm text-slate-600 whitespace-pre-line">{building.address}</div>
            </div>
          </section>
        )}

        {/* On-duty card */}
        <section className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-6 mb-6" data-testid="portal-on-duty-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-emerald-800 mb-2">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> On duty
          </div>
          <div className="font-display text-3xl font-bold tracking-tight text-slate-900">
            {displayName(user?.name)}
          </div>
          <div className="text-sm text-slate-700 mt-1">{user?.email} · {user?.role}</div>
          <div className="text-xs text-slate-500 mt-2">
            Shift started · {shiftStart.toLocaleString()}
          </div>
        </section>

        {/* Actions */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="portal-actions">
          {canOffice && (
          <Link
            to="/kiosk"
            data-testid="portal-open-kiosk"
            className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 hover:bg-slate-100 transition"
          >
            <div>
              <div className="font-display text-base font-semibold text-slate-900">Open kiosk</div>
              <div className="text-xs text-slate-500">Show the visitor check-in / check-out screen on this device</div>
            </div>
            <Monitor className="h-5 w-5 text-slate-500" />
          </Link>
          )}
          {isSchool && canOffice && (
            <Link
              to={isAdmin ? "/admin/buses" : "/bus-status"}
              data-testid="portal-bus-status"
              className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/60 px-5 py-4 hover:bg-amber-50 transition"
            >
              <div>
                <div className="font-display text-base font-semibold text-slate-900">Bus status</div>
                <div className="text-xs text-slate-500">Mark a bus delayed or assign a substitute bus and notify parents</div>
              </div>
              <BusFront className="h-5 w-5 text-amber-600" />
            </Link>
          )}

          {isSchool && canOffice && (
            <Link
              to={isAdmin ? "/admin/forms" : "/forms"}
              data-testid="portal-forms"
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 hover:bg-slate-100 transition"
            >
              <div>
                <div className="font-display text-base font-semibold text-slate-900">Forms</div>
                <div className="text-xs text-slate-500">Create forms and send them to parents to fill and e-sign</div>
              </div>
              <FileSignature className="h-5 w-5 text-slate-500" />
            </Link>
          )}

          {officerHasConsole(user) && (
            <Link
              to={officerAllowedModules(user)[0].path}
              data-testid="portal-open-console"
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 hover:bg-slate-100 transition"
            >
              <div>
                <div className="font-display text-base font-semibold text-slate-900">My console</div>
                <div className="text-xs text-slate-500">{officerAllowedModules(user).map((m) => m.label).join(" · ")}</div>
              </div>
              <LayoutDashboard className="h-5 w-5 text-slate-500" />
            </Link>
          )}

          {isAdmin && (
            <Link
              to="/admin"
              data-testid="portal-open-admin"
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 hover:bg-slate-100 transition"
            >
              <div>
                <div className="font-display text-base font-semibold text-slate-900">Admin dashboard</div>
                <div className="text-xs text-slate-500">View logs, stats, officers and building settings</div>
              </div>
              <LayoutDashboard className="h-5 w-5 text-slate-500" />
            </Link>
          )}

          <button
            onClick={endShift}
            disabled={ending}
            data-testid="portal-end-shift-btn"
            className={`flex items-center justify-between gap-3 rounded-xl border border-destructive/30 bg-white px-5 py-4 text-left hover:bg-destructive/5 transition disabled:opacity-60 ${
              isAdmin ? "" : "sm:col-span-2"
            }`}
          >
            <div>
              <div className="font-display text-base font-semibold text-destructive">End shift &amp; sign out</div>
              <div className="text-xs text-slate-500">Locks the kiosk on this device until the next officer signs in</div>
            </div>
            <Power className="h-5 w-5 text-destructive" />
          </button>
        </section>

        <div className="mt-10 text-xs text-slate-500 text-center">
          Tip: leave this tab open during your shift, or open the kiosk here when visitors arrive.
        </div>
      </div>
    </div>
  );
}
